import React from 'react';

import TagBadge, { TagRow } from '../tags/TagBadge';
import RecordBadge from '../common/RecordBadge';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { normalizeId } from '../../utils/ids';
import { owningCollectionIdOf } from '../../utils/recordQualifier';
import { tagPagePath } from '../../utils/tags';

/**
 * THE TAGS A HISTORY ROW NAMES, AS TAG BADGES (ruled b54, 2026-09-30;
 * docs/platform-standards.md § Tags, § 10). An entry comes from
 * utils/history.js — `historyTagEntries` (one side of a tag-array change) or
 * `historyTagRef` (a tag row's target, a merge's survivor) — and is
 * `{ key, tag, tombstone?, label?, unconfirmed? }`.
 *
 * - A named tag is its `TagBadge`: its tile on its color, its marks (the
 *   lock, and a private tag's muted fill and person glyph), and — where
 *   `linked` — the door to its tag page (`/tags/:id`): in History a click on
 *   a tag means "show me everything with it".
 * - A TOMBSTONE — a deleted tag, one from a collection the reader can't see,
 *   an item tag whose item they can't read — is muted and NEVER a link, in
 *   the words the row gives it.
 * - A tag the vocabulary has not confirmed yet (`unconfirmed`) shows the name
 *   the row recorded, not yet a link.
 * - An item tag is its item's badge when this session knows its card.
 *
 * `linked` is off where leaving would lose work or reach a page the reader
 * can't open: the revert dialog (a decision in progress), and the admin
 * History (an admin opens no tag page by being an admin).
 *
 * A linked chip keeps its click to itself (a bubble-phase stop on a wrapper,
 * never a capture-phase one, which would keep the click from the link — the
 * T5 lesson), so nothing that holds the row reacts to it.
 */
function useItemTagResolver() {
	const cards = useOptionalCardDefinitions();
	const cardsById = cards?.cardsById || null;
	return tag => {
		if (!tag || tag.kind !== 'item') return null;
		const card = cardsById ? cardsById[normalizeId(tag.card_id)] || null : null;
		return { record: card, title: tag.title || 'Untitled item', collection: tag.collection_id || null };
	};
}

export function HistoryTag({ entry, linked = true, size = 'xs' }) {
	const resolveItemTag = useItemTagResolver();
	if (!entry) return null;
	const to = linked && !entry.tombstone && !entry.unconfirmed ? tagPagePath(entry.tag) : null;
	const chip = (
		<TagBadge
			tag={entry.tombstone ? null : entry.tag}
			tombstoneLabel={entry.tombstone ? entry.label : undefined}
			size={size}
			to={to || undefined}
			resolveItemTag={resolveItemTag}
			className="history-tag"
		/>
	);
	if (!to) return chip;
	return (
		<span className="history-tag__isolate" onClick={event => event.stopPropagation()} role="presentation">
			{chip}
		</span>
	);
}

/** Several, as a wrapping row (never a sideways scroll). */
export function HistoryTagRow({ entries = [], linked = true, label = 'Tags' }) {
	const list = Array.isArray(entries) ? entries : [];
	if (!list.length) return null;
	return (
		<TagRow
			className="history-tags"
			tags={list}
			label={label}
			renderTag={entry => <HistoryTag entry={entry} linked={linked} />}
		/>
	);
}

/**
 * A card a History sentence names (a card's item tags): its record badge
 * where the reader's cards include it, else "a card". Never a link — the
 * sentence is about the collection's setting, which the row already opens.
 */
export function HistoryCard({ cardId }) {
	const cards = useOptionalCardDefinitions();
	const card = cards?.cardsById ? cards.cardsById[normalizeId(cardId)] || null : null;
	if (!card) return <span className="history-card history-card--unknown">a card</span>;
	return <RecordBadge kind="card" record={card} title={card.name || 'Card'} size="xs" className="history-card" collection={owningCollectionIdOf(card, 'card')} />;
}
